import React, { useState, useEffect } from "react";
import SpinningLoader from "@src/components/common/SpinningLoader";
import AccountGeneralSettingsForm, {
  UserGeneralSettingsFormErrors,
} from "./AccountGeneralSettingsForm";
import * as UserSettingsApi from "@src/api/UserSettingsApi";
import map from "lodash/map";
import snakeCase from "lodash/snakeCase";
import { useToast } from "@swordsweeper/ssi-ui-component-library";
import { useCurrentUserStore } from "@src/state/currentUserState";

interface UserGeneralSettings {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  initials: string;
  avatarBackgroundColor: string;
  avatarImage: File | string | null;
}

export default function AccountGeneralSettings() {
  const { showToast } = useToast();
  const [isLoadingUserGeneralSettings, setIsLoadingUserGeneralSettings] =
    useState<boolean>(false);
  const [isSavingUserGeneralSettings, setIsSavingUserGeneralSettings] =
    useState<boolean>(false);
  const [userGeneralSettings, setUserGeneralSettings] =
    useState<UserGeneralSettings | null>(null);
  const { currentUser } = useCurrentUserStore();
  const [formErrors, setFormErrors] =
    useState<UserGeneralSettingsFormErrors | null>(null);

  useEffect(() => {
    handleLoadUserGeneralSettings();
  }, []);

  const handleLoadUserGeneralSettings = async () => {
    setIsLoadingUserGeneralSettings(true);
    try {
      const response = await UserSettingsApi.getUserGeneralSettings();
      setUserGeneralSettings(response.body);
    } catch (e) {
      console.error(e);
      showToast({
        message: "Error loading user settings",
        variant: "error",
      });
    } finally {
      setIsLoadingUserGeneralSettings(false);
    }
  };

  const handleSaveUserGeneralSettings = async (
    formData: UserGeneralSettings,
  ) => {
    setIsSavingUserGeneralSettings(true);
    try {
      const formDataForApi = new FormData();
      map(formData, (value, key) => {
        const snakeCaseKey = snakeCase(key);
        if (snakeCaseKey === "avatar_image") {
          if (value instanceof File) {
            // Write the file name with the user's UUID so users don't accidentally override others' images
            const fileExtension = value.name.split(".").pop();
            formDataForApi.append(
              snakeCaseKey,
              value,
              `${currentUser?.id}.${fileExtension}`,
            );
          } else if (value === null) {
            formDataForApi.append(snakeCaseKey, "");
          }
        } else {
          formDataForApi.append(snakeCaseKey, String(value ?? ""));
        }
      });

      const response =
        await UserSettingsApi.setUserGeneralSettings(formDataForApi);
      setUserGeneralSettings(response.body);
      showToast({
        message: "Changes saved",
        variant: "success",
      });
      setFormErrors(null);
    } catch (e) {
      console.error(e);
      setFormErrors(e.response.body);
      showToast({
        message: "Error saving user settings",
        variant: "error",
      });
    } finally {
      setIsSavingUserGeneralSettings(false);
    }
  };

  return (
    <div>
      {(isLoadingUserGeneralSettings || isSavingUserGeneralSettings) && (
        <SpinningLoader />
      )}

      {!!userGeneralSettings && (
        <AccountGeneralSettingsForm
          initialData={userGeneralSettings}
          onSubmit={handleSaveUserGeneralSettings}
          formErrors={formErrors}
        />
      )}
    </div>
  );
}
